import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { EventStatus, type Event, type EventOverview } from '@peles/shared';
import { EventCard } from './EventCard';
import { useEventOverviews } from '../../hooks/useEvents';
import { StatusBadge } from '../common/EntityList';
import { eventStatusColor, eventStatusLabel } from './eventStatus';

/**
 * Cards shown per column before "Show more". Six columns of fifteen stay under
 * the overview endpoint's 100-id limit, so every visible card has its numbers.
 */
export const BOARD_COLUMN_PAGE = 15;

interface EventBoardProps {
  events: Event[];
  /** The columns, in order — the statuses the filter currently shows. */
  statuses: EventStatus[];
  /** Called when a card is dropped on another column. */
  onMove: (event: Event, status: EventStatus) => void;
}

/**
 * The event a drop moves and the column it lands in, or null when the drop
 * changes nothing (dropped outside a column, or back on its own).
 */
export function resolveDrop(
  events: Event[],
  activeId: string | number,
  overId: string | number | undefined,
): { event: Event; status: EventStatus } | null {
  const event = events.find((e) => e.id === activeId);
  if (!event || overId == null || overId === event.status) return null;
  return { event, status: overId as EventStatus };
}

function DraggableCard({ event, overview }: { event: Event; overview?: EventOverview }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: event.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-roledescription="Draggable event"
      className={`touch-none rounded-2xl ${isDragging ? 'opacity-40' : ''}`}
    >
      <EventCard event={event} overview={overview} compact />
    </div>
  );
}

function Column({
  status,
  events,
  total,
  overviews,
  onShowMore,
}: {
  status: EventStatus;
  /** The cards shown so far. */
  events: Event[];
  /** How many events the column holds in all. */
  total: number;
  overviews: Map<string, EventOverview>;
  onShowMore: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const hidden = total - events.length;
  return (
    <section
      ref={setNodeRef}
      aria-label={`${eventStatusLabel(status)} — ${total}`}
      className={`flex flex-col gap-2.5 rounded-2xl p-3 min-h-[16rem] flex-1 min-w-[15rem] transition-colors ${
        isOver ? 'bg-primary-50 ring-2 ring-primary-200' : 'bg-gray-100'
      }`}
    >
      <header className="flex items-center justify-between px-1">
        <StatusBadge label={eventStatusLabel(status)} color={eventStatusColor(status)} />
        <span className="text-xs text-gray-500">{total}</span>
      </header>
      {events.map((e) => (
        <DraggableCard key={e.id} event={e} overview={overviews.get(e.id)} />
      ))}
      {total === 0 && <p className="text-xs text-gray-400 text-center py-6">Drop an event here</p>}
      {hidden > 0 && (
        <button
          type="button"
          onClick={onShowMore}
          className="h-9 rounded-xl text-sm text-primary-700 hover:bg-white/70"
        >
          Show {Math.min(hidden, BOARD_COLUMN_PAGE)} more
        </button>
      )}
    </section>
  );
}

/**
 * The events list's Board view: one column per status. Dragging a card to
 * another column changes the event's status; a short press still opens it,
 * since a drag only starts after the pointer moves a few pixels.
 */
export function EventBoard({ events, statuses, onMove }: EventBoardProps) {
  const [dragging, setDragging] = useState<Event | null>(null);
  // How many cards each column shows; every column starts at one page.
  const [shown, setShown] = useState<Record<string, number>>({});
  const columns = useMemo(
    () =>
      statuses.map((status) => {
        const all = events.filter((e) => e.status === status);
        return { status, all, visible: all.slice(0, shown[status] ?? BOARD_COLUMN_PAGE) };
      }),
    [events, statuses, shown],
  );
  // Numbers only for the cards on screen, so a long board stays one request.
  const { data: overviewList = [] } = useEventOverviews(
    columns.flatMap((c) => c.visible.map((e) => e.id)).slice(0, 100),
  );
  const overviews = useMemo(() => new Map(overviewList.map((o) => [o.eventId, o])), [overviewList]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const onDragStart = ({ active }: DragStartEvent) =>
    setDragging(events.find((e) => e.id === active.id) ?? null);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(null);
    const drop = resolveDrop(events, active.id, over?.id);
    if (drop) onMove(drop.event, drop.status);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="flex gap-4 overflow-x-auto pb-2" data-testid="event-board">
        {columns.map(({ status, all, visible }) => (
          <Column
            key={status}
            status={status}
            events={visible}
            total={all.length}
            overviews={overviews}
            onShowMore={() =>
              setShown((prev) => ({
                ...prev,
                [status]: (prev[status] ?? BOARD_COLUMN_PAGE) + BOARD_COLUMN_PAGE,
              }))
            }
          />
        ))}
      </div>
      <DragOverlay>
        {dragging && (
          <div className="w-72 rotate-1 shadow-xl rounded-2xl">
            <EventCard event={dragging} overview={overviews.get(dragging.id)} compact />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
